import { useLocale, useTranslations } from "use-intl";

import { ReleaseIcon } from "@/components/appIcons";
import { useCuratedCrateName } from "@/components/curated-crate-name";
import {
  buildFooterCrateLinks,
  buildFooterLinkGroups,
  type FooterLinkId,
} from "@/components/footer-links";
import { Link } from "@/components/link";
import { cn } from "@/lib/utils";
import { curatedCrateOffersSearch } from "@garimpa/lib/curatedCrates";
import { normalizeViewerLocale } from "@garimpa/lib/viewerPreferences";

import { CurrencySelector } from "./currency-selector";
import { LanguageSelector } from "./language-selector";

const FOOTER_LINK_CLASS_NAME =
  "inline-flex min-h-11 items-center transition-colors sm:whitespace-nowrap md:min-h-6";

export function Footer() {
  const t = useTranslations("globals.common");
  const th = useTranslations("home");
  const te = useTranslations("featurePageLanding");
  const ta = useTranslations("artistHub");
  const locale = normalizeViewerLocale(useLocale());
  const crateName = useCuratedCrateName();
  const labels: Record<FooterLinkId, string> = {
    offers: th("footerOffers"),
    vinylDeals: th("footerVinyl"),
    cdDeals: th("footerCd"),
    alerts: t("alerts"),
    telegram: t("telegramAlerts"),
    whatsapp: t("whatsappAlerts"),
    editorial: te("eyebrow"),
    collections: te("collection.title"),
    stories: te("story.title"),
    genres: te("genre.title"),
    styles: te("style.title"),
    artists: ta("footerLink"),
    stores: t("stores"),
    providers: t("dataProviders"),
    contact: t("contact"),
    privacy: t("privacy"),
    terms: t("terms"),
  };
  const [dealsGroup, ...otherGroups] = buildFooterLinkGroups(locale);
  const { crates, hubPath } = buildFooterCrateLinks(locale);

  return (
    <footer className="relative z-10 mt-10 border-t border-border bg-card px-4 pb-24 pt-6 xl:pb-6">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-6 md:flex-row md:flex-wrap md:items-start">
        <div className="grid grid-cols-2 gap-x-4 gap-y-6 sm:gap-x-12 lg:grid-cols-5">
          <nav
            aria-labelledby={`footer-${dealsGroup.id}`}
            className="flex flex-col items-start gap-1 text-xs font-semibold"
          >
            <h2
              id={`footer-${dealsGroup.id}`}
              className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground"
            >
              {th("footerGroup", { group: dealsGroup.id })}
            </h2>
            {dealsGroup.links.map((link) => (
              <Link
                key={link.id}
                to={link.to}
                search={link.search}
                className={cn(FOOTER_LINK_CLASS_NAME, link.hoverClassName ?? "hover:text-primary")}
              >
                {labels[link.id]}
              </Link>
            ))}
          </nav>
          <nav
            aria-labelledby="footer-garimpos"
            className="flex flex-col items-start gap-1 text-xs font-semibold"
          >
            <h2
              id="footer-garimpos"
              className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground"
            >
              {th("footerGroup", { group: "garimpos" })}
            </h2>
            {crates.map((crate) => (
              <Link
                key={crate.id}
                to="/offers"
                search={curatedCrateOffersSearch(crate)}
                className={cn(FOOTER_LINK_CLASS_NAME, "hover:text-primary")}
              >
                {crateName(crate)}
              </Link>
            ))}
            <Link to={hubPath} className={cn(FOOTER_LINK_CLASS_NAME, "hover:text-primary")}>
              {th("footerAllGarimpos")}
            </Link>
          </nav>
          {otherGroups.map((group) => (
            <nav
              key={group.id}
              aria-labelledby={`footer-${group.id}`}
              className="flex flex-col items-start gap-1 text-xs font-semibold"
            >
              <h2
                id={`footer-${group.id}`}
                className="mb-1 text-xs font-bold uppercase tracking-widest text-muted-foreground"
              >
                {th("footerGroup", { group: group.id })}
              </h2>
              {group.links.map((link) => (
                <Link
                  key={link.id}
                  to={link.to}
                  search={link.search}
                  className={cn(
                    FOOTER_LINK_CLASS_NAME,
                    link.hoverClassName ?? "hover:text-primary",
                  )}
                >
                  {labels[link.id]}
                </Link>
              ))}
            </nav>
          ))}
        </div>
        <div className="flex flex-col items-center gap-4 text-center md:min-w-60 md:flex-1 md:items-end md:pr-20 md:text-right xl:pr-24">
          <Link
            className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground transition-colors hover:text-foreground"
            to="/"
          >
            <ReleaseIcon className="text-xl" aria-hidden="true" />
            <span>Garimpa.Club</span>
          </Link>
          <div className="flex flex-wrap items-center justify-center gap-3 md:justify-end">
            <LanguageSelector />
            <CurrencySelector />
          </div>
          <p className="max-w-sm text-xs leading-relaxed text-muted-foreground">
            {t("copyrightFull", { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
}
